import { useState, useEffect, createContext, useContext } from 'react';
import authService from '../services/authService';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        const currentUser = authService.getCurrentUser();
        const hasToken = authService.isAuthenticated();

        if (currentUser && hasToken) {
          // Browser refresh - just restore user state without refreshing token
          // Ensure permissions and accessibleFeatures objects exist (handle null/undefined)
          const userWithPermissions = {
            ...currentUser,
            permissions: currentUser.permissions || {},
            accessibleFeatures: currentUser.accessibleFeatures || {
              'Dashboard': false,
              'Purchase Order': false,
              'Inventory': false,
              'Supplier': false,
              'Machine': false,
              'Users': false,
              'My Tenant': false,
              'Reports': false
            }
          };
          setUser(userWithPermissions);
          setIsAuthenticated(true);
          
          // Fetch latest accessible features if not present or if user is authenticated
          if (!userWithPermissions.accessibleFeatures || Object.values(userWithPermissions.accessibleFeatures).every(v => v === false)) {
            try {
              const accessibleFeatures = await authService.getUserPermissions();
              setUser(prev => ({
                ...prev,
                accessibleFeatures
              }));
            } catch (error) {
              console.error('Error fetching accessible features on mount:', error);
            }
          }
          
          // Start automatic token refresh timer if user is authenticated
          authService.startAutoRefresh();
          
          // Note: Token will also be refreshed automatically when API calls are made (401 handler)
          // This avoids unnecessary token refresh calls on browser refresh
        } else {
          // No valid auth data, clear any existing data
          localStorage.removeItem('auth_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user_data');
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear any corrupted data
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user_data');
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  // Listen for permission updates from API service
  useEffect(() => {
    const handlePermissionsUpdate = (event) => {
      const { permissions } = event.detail;
      
      if (permissions && user) {
        // Update user state with new permissions
        setUser(prevUser => ({
          ...prevUser,
          permissions: permissions
        }));
        
        console.info('User permissions updated in auth context');
      }
    };

    window.addEventListener('permissions-updated', handlePermissionsUpdate);

    return () => {
      window.removeEventListener('permissions-updated', handlePermissionsUpdate);
    };
  }, [user]);

  // Login function
  const login = async (credentials) => {
    try {
      setIsLoading(true);
      const { user: userData, token } = await authService.login(credentials);
      
      // Ensure permissions and accessibleFeatures are properly set (handle null/undefined)
      const userWithPermissions = {
        ...userData,
        permissions: userData.permissions || {},
        accessibleFeatures: userData.accessibleFeatures || {
          'Dashboard': false,
          'Purchase Order': false,
          'Inventory': false,
          'Supplier': false,
          'Machine': false,
          'Users': false,
          'My Tenant': false,
          'Reports': false
        }
      };
      
      setUser(userWithPermissions);
      setIsAuthenticated(true);
      
      return { user: userWithPermissions, token };
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      setIsLoading(true);
      const { user: newUser, token } = await authService.register(userData);
      
      setUser(newUser);
      setIsAuthenticated(true);
      
      return { user: newUser, token };
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      await authService.logout(true); // Show toast for user-initiated logout
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Update user profile
  const updateProfile = async (userData) => {
    try {
      setIsLoading(true);
      const updatedUser = await authService.updateProfile(userData);
      
      setUser(updatedUser);
      
      return updatedUser;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch and merge profile data
  const fetchProfileData = async () => {
    try {
      setIsLoading(true);
      const userWithProfile = await authService.fetchAndMergeProfileData();
      setUser(userWithProfile);
      return userWithProfile;
    } catch (error) {
      console.error('Error fetching profile data:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    try {
      setIsLoading(true);
      const message = await authService.changePassword(currentPassword, newPassword);
      
      return message;
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Forgot password
  const forgotPassword = async (email) => {
    try {
      setIsLoading(true);
      const message = await authService.forgotPassword(email);
      
      return message;
    } catch (error) {
      console.error('Forgot password error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (token, newPassword) => {
    try {
      setIsLoading(true);
      const message = await authService.resetPassword(token, newPassword);
      
      return message;
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Refresh token
  const refreshToken = async () => {
    try {
      const newToken = await authService.refreshToken();
      return newToken;
    } catch (error) {
      console.error('Token refresh error:', error);
      // Don't call logout here - let the component handle it appropriately
      throw error;
    }
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
    fetchProfileData,
    changePassword,
    forgotPassword,
    resetPassword,
    refreshToken,
    hasPermission: (permissionKey) => user?.permissions?.[permissionKey] === true || user?.permissions?.[permissionKey] === 1,
    hasRole: (requiredRoles) => requiredRoles.includes(user?.role),
    getUserPermissions: () => user?.permissions || null,
    accessibleFeatures: user?.accessibleFeatures || {
      'Dashboard': false,
      'Purchase Order': false,
      'Inventory': false,
      'Supplier': false,
      'Machine': false,
      'Users': false,
      'My Tenant': false,
      'Reports': false
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
};

// Higher-order component for protected routes
export const withAuth = (WrappedComponent) => {
  return function AuthenticatedComponent(props) {
    const { isAuthenticated, isLoading } = useAuth();
    
    if (isLoading) {
      return (
        <div className="loading-container">
          <div className="gear-loader">Loading...</div>
        </div>
      );
    }
    
    if (!isAuthenticated) {
      // Redirect to login or show login form
      return <div>Please login to access this page.</div>;
    }
    
    return <WrappedComponent {...props} />;
  };
};

export default useAuth;
